/**
 * Dynamic cart cross-sells hydrator.
 *
 * When `settings.cross_sells_recommendations_type` is `'dynamic'`,
 * `snippets/cross-sells.liquid` renders an empty container with the
 * `[data-dynamic-cross-sells]` attribute. This script:
 *   1. Watches for cart drawer opens and cart updates
 *   2. Fetches per-product complementary recommendations from
 *      `/recommendations/products` using Shopify's section rendering
 *      pattern (intent=complementary, falls back to intent=related)
 *   3. Parses the response and moves the rendered nodes into the container
 *      using node-level DOM operations (no innerHTML of fetched content)
 *
 * The section rendered server-side is `sections/cart-cross-sells-dynamic.liquid`.
 */
(function () {
  'use strict';

  var SECTION_ID = 'cart-cross-sells-dynamic';
  var CONTAINER_SELECTOR = '[data-dynamic-cross-sells]';
  var CONTENT_SELECTOR = '[data-dynamic-cross-sells-content]';
  var DEFAULT_LIMIT = 4;

  // Cache keyed by `${productId}-${intent}-${limit}` so we don't re-fetch
  // the same recommendations within a single page load. Stores the parsed
  // inner element (cloned fresh on each use).
  var cache = {};

  // Most recently added item is tracked via the `cart:item-added` DOM event
  // dispatched by theme.js after a successful ATC. This is the most reliable
  // signal for "what is the user thinking about right now" and avoids having
  // to rely on Shopify's cart.items ordering (which varies depending on the
  // API used to mutate the cart).
  var lastAddedProductId = null;
  document.addEventListener('cart:item-added', function (evt) {
    if (evt && evt.detail && evt.detail.product && evt.detail.product.product_id) {
      lastAddedProductId = evt.detail.product.product_id;
    } else if (evt && evt.detail && evt.detail.product && evt.detail.product.id) {
      // Some Shopify endpoints return the line item with `id` rather than
      // `product_id`; fall back to that if needed.
      lastAddedProductId = evt.detail.product.product_id || evt.detail.product.id;
    }
  });

  function getCartContext() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items || cart.items.length === 0) return null;

        // Collect all in-cart product IDs so we can filter them out of the
        // recommendation response — no point recommending something that's
        // already in the cart.
        var allProductIds = {};
        for (var i = 0; i < cart.items.length; i++) {
          allProductIds[String(cart.items[i].product_id)] = true;
        }

        // Prefer the explicitly-tracked last-added product (via cart:item-added
        // event). If we haven't seen that event yet (e.g. initial page load
        // with a pre-existing cart), fall back to cart.items[0] — Shopify's
        // Ajax API returns cart items newest-first, so index 0 is the most
        // recently added item.
        var anchor = null;
        if (lastAddedProductId) {
          for (var j = 0; j < cart.items.length; j++) {
            if (cart.items[j].product_id === lastAddedProductId) {
              anchor = cart.items[j];
              break;
            }
          }
        }
        if (!anchor) anchor = cart.items[0];

        return { anchor: anchor, cartProductIds: allProductIds };
      })
      .catch(function () { return null; });
  }

  function filterCartItemsFromNodes(sourceEl, cartProductIds) {
    // Clone the source element so we don't mutate the cached version.
    var clone = sourceEl.cloneNode(true);
    var items = clone.querySelectorAll('[data-product-id]');
    for (var i = items.length - 1; i >= 0; i--) {
      var pid = items[i].getAttribute('data-product-id');
      if (pid && cartProductIds[pid]) {
        items[i].parentNode.removeChild(items[i]);
      }
    }
    return clone;
  }

  function fetchRecommendationsNodes(productId, intent, limit) {
    var cacheKey = productId + '-' + intent + '-' + limit;
    if (cache[cacheKey]) return Promise.resolve(cache[cacheKey]);

    var url = '/recommendations/products'
      + '?product_id=' + encodeURIComponent(productId)
      + '&intent=' + encodeURIComponent(intent)
      + '&limit=' + encodeURIComponent(limit)
      + '&section_id=' + encodeURIComponent(SECTION_ID);

    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) return null;
        // Parse the section HTML in an inert DOMParser document — nodes are
        // not connected to the live document and script/iframe tags don't
        // execute. We then move only the children of the content marker into
        // the live DOM via appendChild, never assigning innerHTML from the
        // fetched string.
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var inner = doc.querySelector(CONTENT_SELECTOR);
        if (!inner || inner.children.length === 0) return null;
        cache[cacheKey] = inner;
        return inner;
      })
      .catch(function () { return null; });
  }

  function clearContainer(container) {
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
  }

  function moveChildrenInto(sourceEl, targetEl) {
    clearContainer(targetEl);
    // Clone each child so the cached element can be reused on next hydration.
    // Also tag every .cross-sells__item we inject so our delegated click
    // handler below knows to handle Add button clicks (the theme's CrossSells
    // module attached listeners only to the server-rendered static items,
    // which we've just replaced with these dynamic ones).
    for (var i = 0; i < sourceEl.children.length; i++) {
      var clone = sourceEl.children[i].cloneNode(true);
      var markItems = clone.querySelectorAll
        ? clone.querySelectorAll('.cross-sells__item')
        : [];
      for (var j = 0; j < markItems.length; j++) {
        markItems[j].setAttribute('data-dcs-hydrated', '1');
      }
      targetEl.appendChild(clone);
    }
  }

  // ============================================================================
  // Event delegation for dynamically-injected Add buttons.
  //
  // The theme's CrossSells module (theme.js ~line 9722) attaches direct click
  // listeners to each .cross-sells__item-add at initialization time. Items we
  // inject later have no such listeners — so without this, clicking Add on a
  // dynamic cross-sell silently does nothing. This is the bug the user hit
  // when trying to add recommended products from the cart drawer.
  //
  // We use document-level delegation so the listener survives any DOM
  // replacement (drawer refreshes, re-hydration, etc.). The marker check
  // `data-dcs-hydrated` ensures we only handle items we injected; static
  // fallback items still go through the theme's own handlers.
  // ============================================================================
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target && e.target.closest && e.target.closest('.cross-sells__item-add');
      if (!btn) return;
      var itemEl = btn.closest('[data-dcs-hydrated="1"]');
      if (!itemEl) return; // static fallback item, theme handles it
      var dynContainer = btn.closest('[data-dynamic-cross-sells]');
      if (!dynContainer) return;

      // Stop the theme's own delegated handler (if any) from also running.
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();

      var addItemId = btn.getAttribute('data-add-item-id');
      var productUrl = btn.getAttribute('data-product-url');

      if (addItemId) {
        handleDynamicAddToCart(btn, addItemId);
      } else if (productUrl) {
        // Variant product: no default variant, so the user has to pick one.
        // Navigate to the PDP where the full variant picker lives.
        window.location.href = productUrl;
      }
    },
    true // capture phase so we win the race against theme's delegated handlers
  );

  function handleDynamicAddToCart(btn, variantId) {
    if (btn.dataset.dcsLoading === '1') return;
    btn.dataset.dcsLoading = '1';
    btn.classList.add('loading');

    // Use the patched window.fetch so universal-cart-open.js's intercept
    // also fires (it refreshes quick-add-in-cart quantities on the page).
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed: ' + r.status);
        return r.json();
      })
      .then(function (item) {
        btn.classList.remove('loading');
        btn.dataset.dcsLoading = '0';
        // Dispatch DOM events so any listener (theme or custom) can react.
        try {
          document.dispatchEvent(
            new CustomEvent('cart:item-added', { detail: { product: item } })
          );
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: {} }));
        } catch (e) {
          /* IE fallback not needed in modern Shopify */
        }
        // The theme's drawer-refresh logic is wired to an internal event bus
        // we can't call from outside, so refresh the drawer contents ourselves
        // using Shopify's section-rendering API.
        refreshDrawerFromServer();
      })
      .catch(function () {
        btn.classList.remove('loading');
        btn.dataset.dcsLoading = '0';
      });
  }

  function refreshDrawerFromServer() {
    var section = document.querySelector('.quick-cart[data-section-id]');
    if (!section) return;
    var sectionId = section.getAttribute('data-section-id');
    if (!sectionId) return;

    fetch('/cart?section_id=' + encodeURIComponent(sectionId), {
      credentials: 'same-origin',
    })
      .then(function (r) {
        return r.ok ? r.text() : '';
      })
      .then(function (html) {
        if (!html) return;
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        // Replace just the inner contents of the current drawer wrapper,
        // preserving its .active class so the drawer stays open.
        var freshWrapper = doc.querySelector('.quick-cart__wrapper');
        var currentWrapper = section.querySelector('.quick-cart__wrapper');
        if (!freshWrapper || !currentWrapper) return;

        var wasActive = currentWrapper.classList.contains('active');
        while (currentWrapper.firstChild) {
          currentWrapper.removeChild(currentWrapper.firstChild);
        }
        for (var i = 0; i < freshWrapper.children.length; i++) {
          currentWrapper.appendChild(freshWrapper.children[i].cloneNode(true));
        }
        if (wasActive) currentWrapper.classList.add('active');

        // The fresh server-rendered drawer includes static fallback cross-sells.
        // Re-run our dynamic hydration to replace them with per-product
        // recommendations for the new cart anchor.
        setTimeout(function () {
          var containers = document.querySelectorAll('[data-dynamic-cross-sells]');
          for (var k = 0; k < containers.length; k++) {
            hydrateContainer(containers[k]);
          }
        }, 150);
      })
      .catch(function () {});
  }

  function hydrateContainer(container) {
    if (!container || container.dataset.hydrating === 'true') return;
    container.dataset.hydrating = 'true';

    var limit = parseInt(container.dataset.recommendationsLimit, 10) || DEFAULT_LIMIT;
    // Over-fetch so we still have enough items after filtering out
    // products that are already in the cart. Shopify caps at ~10 anyway.
    var fetchLimit = limit + 6;

    // The container is pre-populated with the static `product_list` fallback
    // from cross-sells.liquid. If dynamic hydration succeeds, we replace those
    // children with the dynamic ones. If hydration fails OR returns zero
    // usable items after filtering, we leave the static fallback in place —
    // the slot always shows SOMETHING.

    getCartContext()
      .then(function (ctx) {
        if (!ctx) return null;
        // Try merchant-set complementary first, fall back to algorithmic related.
        return fetchRecommendationsNodes(ctx.anchor.product_id, 'complementary', fetchLimit)
          .then(function (nodes) {
            if (nodes) return { nodes: nodes, cartIds: ctx.cartProductIds };
            return fetchRecommendationsNodes(ctx.anchor.product_id, 'related', fetchLimit)
              .then(function (fallbackNodes) {
                return fallbackNodes ? { nodes: fallbackNodes, cartIds: ctx.cartProductIds } : null;
              });
          });
      })
      .then(function (result) {
        if (result && result.nodes) {
          var filtered = filterCartItemsFromNodes(result.nodes, result.cartIds);
          // Count actual cross-sell items after filtering. The section
          // markup nests them a few levels deep (dynamic-inner > inner >
          // items > item), so we need to query descendants, not children.
          var itemEls = filtered.querySelectorAll('.cross-sells__item');
          if (itemEls.length > 0) {
            // Trim to the configured limit after filtering.
            for (var k = itemEls.length - 1; k >= limit; k--) {
              itemEls[k].parentNode.removeChild(itemEls[k]);
            }
            moveChildrenInto(filtered, container);
          }
          // else: leave the static server-rendered fallback in place
        }
        // else: network failure or empty — leave the static fallback in place
        container.dataset.hydrating = 'false';
      })
      .catch(function () {
        // Leave the static fallback in place on any error.
        container.dataset.hydrating = 'false';
      });
  }

  function hydrateAll() {
    var containers = document.querySelectorAll(CONTAINER_SELECTOR);
    for (var i = 0; i < containers.length; i++) {
      hydrateContainer(containers[i]);
    }
  }

  // Expose for debugging and for universal-cart-open.js to call after
  // successful cart mutations (more reliable than waiting for the theme's
  // cart:item-added DOM event, which in some paths doesn't fire before
  // the drawer re-renders).
  window.__dcsHydrateAll = hydrateAll;
  window.__dcsDebug = { listenerFires: 0, hydrateCalls: 0 };

  // Initial hydration on DOM ready (covers the case where the drawer was
  // server-rendered with an empty dynamic container already present).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrateAll);
  } else {
    hydrateAll();
  }

  // Re-hydrate whenever the cart changes — the quick-cart drawer re-renders
  // its content via AJAX, creating a fresh empty container each time. Small
  // delay so the drawer's own refresh completes first.
  document.addEventListener('cart:item-added', function () {
    window.__dcsDebug.listenerFires++;
    setTimeout(function () {
      window.__dcsDebug.hydrateCalls++;
      hydrateAll();
    }, 300);
  });
  document.addEventListener('cart:updated', function () {
    window.__dcsDebug.listenerFires++;
    setTimeout(function () {
      window.__dcsDebug.hydrateCalls++;
      hydrateAll();
    }, 300);
  });

  // ALSO intercept fetch to trigger hydration after cart mutations. Belt and
  // braces — if the DOM events aren't being fired by the theme for whatever
  // reason (minified theme code paths are opaque), this catches everything
  // at the network layer.
  var _origFetch = window.fetch;
  if (_origFetch && !_origFetch.__dcs_patched) {
    window.fetch = function (url, opts) {
      var result = _origFetch.apply(this, arguments);
      var u = typeof url === 'string' ? url : (url && url.url) || '';
      if (/\/cart\/(add|change|update|clear)\.js/.test(u)) {
        result.then(function (r) {
          if (r && (r.ok || r.status === 200)) {
            window.__dcsDebug.fetchHydrateTriggers = (window.__dcsDebug.fetchHydrateTriggers || 0) + 1;
            // Give any drawer-refresh JS time to complete, then re-hydrate.
            setTimeout(hydrateAll, 500);
          }
        }).catch(function () {});
      }
      return result;
    };
    window.fetch.__dcs_patched = true;
  }

  // Watch for the quick-cart drawer becoming active (class toggles). When it
  // does, hydrate any empty dynamic containers inside it.
  var drawerObserver = new MutationObserver(function () {
    var activeDrawer = document.querySelector('.quick-cart__wrapper.active');
    if (activeDrawer) {
      var containers = activeDrawer.querySelectorAll(CONTAINER_SELECTOR);
      for (var i = 0; i < containers.length; i++) {
        if (containers[i].children.length === 0 && containers[i].dataset.hydrating !== 'true') {
          hydrateContainer(containers[i]);
        }
      }
    }
  });

  if (document.body) {
    drawerObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    });
  }
})();
